<script lang="ts">
  import { onMount } from 'svelte';
  import { push } from 'svelte-spa-router';
  import Table from '../../Table.svelte';
  import RecentPendingPayouts from './RecentPendingPayouts.svelte';
  import RecentPendingConfirmation from './RecentPendingConfirmation.svelte';
  import RecentUnconfirmedBankDetails from './RecentUnconfirmedBankDetails.svelte';
  import {
    adminDashboardService,
    type AdminDashboardData,
    type RecentPendingConfirmationCharity,
    type RecentPendingPayout,
    type RecentRequest,
    type RecentUnconfirmedBankDetails
  } from '../../../lib/api/admin/dashboard.service';
  import { setAdminPendingActions } from '../../../stores/admin-pending-actions.store';
  import { getStageMeta, stageFromStatusName, type LifecycleStage } from '../../../lib/donationLifecycle';

  type MetricLink = { href: string; aria: string };
  type DashboardTab = 'pending-payouts' | 'pending-confirmation' | 'unconfirmed-bank-details';

  const EMPLOYEE_USERS_URL = '/admin/users?role=employee';

  let loading = true;
  let error: string | null = null;
  let dashboardData: AdminDashboardData | null = null;

  let pendingPayoutsCount = 0;
  let pendingPayoutsValue = 0;
  let pendingConfirmationCount = 0;
  let pendingConfirmationValue = 0;
  let unconfirmedBankDetailsCount = 0;
  let charitiesInvitedCount = 0;
  let employersInvitedCount = 0;

  let recentPendingPayouts: RecentPendingPayout[] = [];
  let recentPendingConfirmation: RecentPendingConfirmationCharity[] = [];
  let recentUnconfirmedBankDetails: RecentUnconfirmedBankDetails[] = [];

  let activeTab: DashboardTab = 'pending-payouts';

  $: showPendingPayoutsBanner =
    !loading && (pendingPayoutsCount > 0 || pendingPayoutsValue > 0);
  $: pendingPayoutsBannerLabel =
    pendingPayoutsCount === 1
      ? '1 payout awaiting your action'
      : pendingPayoutsCount > 1
        ? `${pendingPayoutsCount} payouts awaiting your action`
        : 'Payouts awaiting your action';

  $: showPendingConfirmationBanner =
    !loading && (pendingConfirmationCount > 0 || pendingConfirmationValue > 0);
  $: pendingConfirmationBannerLabel =
    pendingConfirmationCount === 1
      ? '1 payment awaiting charity confirmation'
      : pendingConfirmationCount > 1
        ? `${pendingConfirmationCount} payments awaiting charity confirmation`
        : 'Payments awaiting charity confirmation';

  $: showUnconfirmedBankBanner = !loading && unconfirmedBankDetailsCount > 0;
  $: unconfirmedBankBannerLabel =
    unconfirmedBankDetailsCount === 1
      ? '1 charity has unconfirmed bank details'
      : `${unconfirmedBankDetailsCount} charities have unconfirmed bank details`;

  let dashboardMetrics = [
    {
      id: 'pending-payouts',
      link: { href: '/admin/pending-payouts', aria: 'Review pending payouts' } as MetricLink,
      label: 'Pending Payouts',
      value: '$0.00',
      icon: 'M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z',
      bgColor: 'bg-red-custom',
      iconColor: 'text-white',
      dotColor: '#FC6C6B',
      viewbox: '0 0 24 24'
    },
    {
      id: 'pending-confirmation',
      link: {
        href: '/admin/pending-confirmation',
        aria: 'View charities awaiting confirmation'
      } as MetricLink,
      label: 'Pending Confirmation',
      value: '$0.00',
      icon: 'M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67z',
      bgColor: 'bg-purple-custom',
      iconColor: 'text-white',
      dotColor: '#8B5CF6',
      viewbox: '0 0 24 24'
    },
    {
      id: 'unconfirmed-bank-details',
      link: {
        href: '/admin/unconfirmed-bank-details',
        aria: 'Review unconfirmed bank details'
      } as MetricLink,
      label: 'Unconfirmed Bank Details',
      value: '0',
      icon: 'M20 4H4c-1.11 0-1.99.89-1.99 2L2 18c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V6c0-1.11-.89-2-2-2zm0 14H4v-6h16v6zm0-10H4V6h16v2z',
      bgColor: 'bg-amber-custom',
      iconColor: 'text-white',
      dotColor: '#FCB201',
      viewbox: '0 0 24 24'
    }
  ];

  let overviewMetrics = [
    {
      id: 'totalDonations',
      label: 'Total paid out',
      value: '$0',
      href: '/admin/payments/outgoing'
    },
    {
      id: 'pendingRequests',
      label: 'Pending Requests',
      value: '0',
      href: '/donation-list'
    },
    {
      id: 'registeredUsers',
      label: 'Registered Users',
      value: '0',
      href: EMPLOYEE_USERS_URL
    }
  ];

  $: recentRequests =
    dashboardData?.recentRequests.map((request: RecentRequest) => ({
      requestId: request.requestId,
      employeeName: request.employeeName,
      leaveHours: `${request.leaveHours}h`,
      date: request.date,
      status: request.status,
      stage: (request.stage as LifecycleStage) || stageFromStatusName(request.status),
      waitingOn: request.waitingOn || getFallbackWaitingOn(request.status),
      uuid: request.requestId
    })) || [];

  $: maxValue = 1000000;
  $: yAxisValues = [1000000, 500000, 250000, 100000, 0];

  const tableColumns = [
    {
      key: 'requestId',
      label: 'Request ID',
      width: '15%',
      render: (value: string) => {
        const shortId = value.substring(0, 8);
        return `<span class="request-id" title="${value}">${shortId}...</span>`;
      }
    },
    {
      key: 'employeeName',
      label: 'Employee Name',
      width: '20%',
      render: (value: string) => `<span class="employee-name">${value}</span>`
    },
    {
      key: 'leaveHours',
      label: 'Leave Hours',
      width: '15%'
    },
    {
      key: 'date',
      label: 'Date',
      width: '12%'
    },
    {
      key: 'waitingOn',
      label: 'With',
      width: '16%',
      render: (value: string) => `<span class="waiting-on">${value || '—'}</span>`
    },
    {
      key: 'statusLabel',
      label: 'Status',
      width: '26%',
      align: 'center',
      render: (_value: string, row: { stage?: LifecycleStage; status: string }) =>
        renderStageBadge(row.stage || stageFromStatusName(row.status))
    }
  ];

  function formatCurrency(amount: number): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  }

  function updateDashboardMetrics() {
    dashboardMetrics = dashboardMetrics.map((metric) => {
      if (metric.id === 'pending-payouts') {
        return { ...metric, value: formatCurrency(pendingPayoutsValue) };
      }
      if (metric.id === 'pending-confirmation') {
        return { ...metric, value: formatCurrency(pendingConfirmationValue) };
      }
      if (metric.id === 'unconfirmed-bank-details') {
        return { ...metric, value: String(unconfirmedBankDetailsCount) };
      }
      return metric;
    });
  }

  function updateOverviewMetrics() {
    if (!dashboardData) return;
    overviewMetrics = overviewMetrics.map((metric) => {
      if (metric.id === 'totalDonations') {
        return { ...metric, value: formatCurrency(dashboardData!.summary.totalDonations) };
      }
      if (metric.id === 'pendingRequests') {
        return { ...metric, value: dashboardData!.summary.pendingRequests.toString() };
      }
      if (metric.id === 'registeredUsers') {
        return { ...metric, value: dashboardData!.summary.registeredUsers.toString() };
      }
      return metric;
    });
  }

  async function loadDashboardData() {
    try {
      loading = true;
      error = null;

      const result = await adminDashboardService.getDashboardData();

      if (result.success && result.data) {
        dashboardData = result.data;
        pendingPayoutsCount =
          result.data.summary.pendingPayoutsCount ??
          result.data.summary.awaitingL2gPayout ??
          0;
        pendingPayoutsValue = result.data.summary.pendingPayoutsValue ?? 0;
        pendingConfirmationCount =
          result.data.summary.pendingConfirmationCount ??
          result.data.summary.awaitingCharityConfirmation ??
          0;
        pendingConfirmationValue = result.data.summary.pendingConfirmationValue ?? 0;
        unconfirmedBankDetailsCount = result.data.summary.unconfirmedBankDetailsCount ?? 0;
        charitiesInvitedCount = result.data.summary.charitiesInvitedCount ?? 0;
        employersInvitedCount = result.data.summary.employersInvitedCount ?? 0;

        recentPendingPayouts = result.data.recentPendingPayouts ?? [];
        recentPendingConfirmation = result.data.recentPendingConfirmation ?? [];
        recentUnconfirmedBankDetails = result.data.recentUnconfirmedBankDetails ?? [];

        setAdminPendingActions(result.data.summary);
        updateDashboardMetrics();
        updateOverviewMetrics();
      } else {
        error = result.message || 'Failed to load dashboard data';
      }
    } catch (err: unknown) {
      error = err instanceof Error ? err.message : 'An unexpected error occurred';
    } finally {
      loading = false;
    }
  }

  onMount(() => {
    loadDashboardData();
  });

  function renderStageBadge(stage: LifecycleStage): string {
    const meta = getStageMeta(stage);
    const tick =
      '<svg viewBox="0 0 20 20" width="16" height="16" fill="currentColor"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/></svg>';
    const cross =
      '<svg viewBox="0 0 20 20" width="16" height="16" fill="currentColor"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"/></svg>';
    const clock =
      '<svg viewBox="0 0 20 20" width="16" height="16" fill="currentColor"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clip-rule="evenodd"/></svg>';
    const icon = meta.tone === 'red' ? cross : meta.tone === 'amber' || meta.tone === 'grey' ? clock : tick;
    return `<span class="lc-badge ${meta.badgeClass}">${icon}<span>${meta.label}</span></span>`;
  }

  function getFallbackWaitingOn(status: string): string {
    switch (status?.toUpperCase()) {
      case 'APPROVED':
        return 'Employer payment';
      case 'PROCESSED':
      case 'COMPLETED':
        return 'Charity confirmation';
      case 'REJECTED':
        return 'Closed';
      case 'PENDING':
      default:
        return 'Employer';
    }
  }

  function handleRowClick(row: { uuid?: string }) {
    if (row.uuid) {
      push(`/view-donation/${row.uuid}`);
    }
  }

  function viewAllDonations() {
    push('/donation-list');
  }
</script>

<div class="admin-dashboard">
  {#if loading}
    <div class="loading-container">
      <div class="loading-spinner"></div>
      <p>Loading dashboard data...</p>
    </div>
  {:else if error}
    <div class="error-container">
      <div class="error-icon" aria-hidden="true">⚠️</div>
      <h3>Unable to Load Dashboard</h3>
      <p>{error}</p>
      <button class="retry-button" type="button" on:click={loadDashboardData}>Try Again</button>
    </div>
  {:else if dashboardData}
    <div class="dashboard-header">
      <p class="subtitle">Welcome to your admin dashboard</p>
    </div>

    <div class="dashboard-alerts">
      {#if showPendingPayoutsBanner}
        <div class="pending-payouts-banner banner-full-width" role="status" aria-live="polite">
          <div class="banner-copy">
            <span class="banner-eyebrow">Action required</span>
            <p class="banner-title">{pendingPayoutsBannerLabel}</p>
            <p class="banner-text">
              {formatCurrency(pendingPayoutsValue)} from employer payments is ready to pay out to
              charities. Mark payouts as paid once transfers are complete.
            </p>
          </div>
          <button type="button" class="banner-action" on:click={() => push('/admin/pending-payouts')}>
            Review payouts
          </button>
        </div>
      {/if}

      {#if showPendingConfirmationBanner || showUnconfirmedBankBanner}
        <div class="action-banners-grid">
          {#if showPendingConfirmationBanner}
            <div class="pending-confirmation-banner dashboard-banner" role="status" aria-live="polite">
              <div class="banner-copy">
                <span class="banner-eyebrow">Waiting on charities</span>
                <p class="banner-title">{pendingConfirmationBannerLabel}</p>
                <p class="banner-text">
                  {formatCurrency(pendingConfirmationValue)} has been paid to charities and is awaiting
                  their confirmation that funds were received.
                </p>
              </div>
              <button
                type="button"
                class="banner-action"
                on:click={() => push('/admin/pending-confirmation')}
              >
                View charities
              </button>
            </div>
          {/if}

          {#if showUnconfirmedBankBanner}
            <div class="unconfirmed-bank-banner dashboard-banner" role="status" aria-live="polite">
              <div class="banner-copy">
                <span class="banner-eyebrow">Verification needed</span>
                <p class="banner-title">{unconfirmedBankBannerLabel}</p>
                <p class="banner-text">
                  These charities have bank details on file that still need phone verification before
                  payouts can proceed safely.
                </p>
              </div>
              <button
                type="button"
                class="banner-action"
                on:click={() => push('/admin/unconfirmed-bank-details')}
              >
                Review bank details
              </button>
            </div>
          {/if}
        </div>
      {/if}

      <div class="verification-links-grid">
        <a
          class="verification-link-card charities-pending-card"
          href="/admin/verification/charities"
          aria-label="Charities pending approval: {charitiesInvitedCount} invited"
          on:click|preventDefault={() => push('/admin/verification/charities')}
        >
          <div class="verification-link-copy">
            <span class="verification-link-eyebrow">Registration</span>
            <p class="verification-link-title">Charities pending approval</p>
            <p class="verification-link-text">
              {charitiesInvitedCount === 1
                ? '1 charity invited but not yet registered'
                : `${charitiesInvitedCount} charities invited but not yet registered`}
            </p>
          </div>
          <span class="verification-link-action">Review charities</span>
        </a>

        <a
          class="verification-link-card employers-pending-card"
          href="/admin/verification/employers"
          aria-label="Employers pending approval: {employersInvitedCount} invited"
          on:click|preventDefault={() => push('/admin/verification/employers')}
        >
          <div class="verification-link-copy">
            <span class="verification-link-eyebrow">Registration</span>
            <p class="verification-link-title">Employers pending approval</p>
            <p class="verification-link-text">
              {employersInvitedCount === 1
                ? '1 employer invited but not yet registered'
                : `${employersInvitedCount} employers invited but not yet registered`}
            </p>
          </div>
          <span class="verification-link-action">Review employers</span>
        </a>
      </div>
    </div>

    <div class="metrics-grid">
      {#each dashboardMetrics as metric}
        <a
          class="metric-card metric-card-link"
          href={metric.link.href}
          aria-label="{metric.label}: {metric.value}. {metric.link.aria}."
          on:click|preventDefault={() => push(metric.link.href)}
          on:keydown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              push(metric.link.href);
            }
          }}
        >
          <div class="metric-content">
            <div class="metric-left">
              <div class="metric-label-container">
                <span class="metric-dot" style="background-color: {metric.dotColor}"></span>
                <span class="metric-label">{metric.label}</span>
              </div>
              <div class="metric-value">{metric.value}</div>
            </div>
            <div class="metric-icon {metric.bgColor}" aria-hidden="true">
              <svg class="icon {metric.iconColor}" viewBox={metric.viewbox} fill="currentColor">
                <path d={metric.icon} />
              </svg>
            </div>
          </div>
        </a>
      {/each}
    </div>

    <div class="dashboard-content">
      <div class="tabs-container">
        <div class="tabs">
          <button
            type="button"
            class="tab-button"
            class:active={activeTab === 'pending-payouts'}
            on:click={() => (activeTab = 'pending-payouts')}
          >
            Pending Payouts
          </button>
          <button
            type="button"
            class="tab-button"
            class:active={activeTab === 'pending-confirmation'}
            on:click={() => (activeTab = 'pending-confirmation')}
          >
            Pending Confirmation
          </button>
          <button
            type="button"
            class="tab-button"
            class:active={activeTab === 'unconfirmed-bank-details'}
            on:click={() => (activeTab = 'unconfirmed-bank-details')}
          >
            Unconfirmed Bank Details
          </button>
        </div>
      </div>

      {#if activeTab === 'pending-payouts'}
        <RecentPendingPayouts payouts={recentPendingPayouts} {loading} {error} />
      {:else if activeTab === 'pending-confirmation'}
        <RecentPendingConfirmation charities={recentPendingConfirmation} {loading} {error} />
      {:else}
        <RecentUnconfirmedBankDetails charities={recentUnconfirmedBankDetails} {loading} {error} />
      {/if}
    </div>

    <section class="overview-section" aria-labelledby="overview-heading">
      <h2 id="overview-heading" class="section-heading">Overview</h2>

      <div class="overview-metrics-grid">
        {#each overviewMetrics as metric}
          <a
            class="overview-metric-card"
            href={metric.href}
            on:click|preventDefault={() => push(metric.href)}
          >
            <span class="overview-metric-label">{metric.label}</span>
            <span class="overview-metric-value">{metric.value}</span>
          </a>
        {/each}
      </div>

      <div class="charts-section">
        <div class="chart-card trends-card">
          <div class="chart-header">
            <h3>Donation Trends</h3>
          </div>
          <div class="chart-content">
            <div class="chart-wrapper">
              <div class="y-axis-labels">
                {#each yAxisValues as value}
                  <div class="y-label">{value.toLocaleString()}</div>
                {/each}
              </div>
              <div class="chart-bars-area">
                <div class="grid-lines">
                  {#each yAxisValues as _value, index}
                    {@const topPosition = (index / (yAxisValues.length - 1)) * 100}
                    <div class="grid-line" style="top: {topPosition}%"></div>
                  {/each}
                </div>
                <div class="bars-wrapper">
                  {#each dashboardData.donationTrends as trend}
                    {@const clampedAmount = Math.min(trend.amount, maxValue)}
                    {@const barHeight = maxValue > 0 ? (clampedAmount / maxValue) * 100 : 0}
                    {@const isOverflow = trend.amount > maxValue}
                    <div class="bar-column">
                      <div class="bar background-bar"></div>
                      <div class="bar foreground-bar" style="height: {barHeight}%">
                        <div class="bar-value">${Math.round(trend.amount).toLocaleString()}</div>
                        {#if isOverflow}
                          <div class="overflow-indicator">+</div>
                        {/if}
                      </div>
                    </div>
                  {/each}
                </div>
                <div class="x-axis-labels">
                  {#each dashboardData.donationTrends as trend}
                    <div class="x-label">{trend.month}</div>
                  {/each}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="chart-card top-charities-card">
          <div class="chart-header">
            <h3>Top 3 Charities by Donation</h3>
          </div>
          <div class="top-charities-content">
            {#if dashboardData.topCharities && dashboardData.topCharities.length > 0}
              <div class="charities-list">
                {#each dashboardData.topCharities as charity, index}
                  <div
                    class="charity-item"
                    role="button"
                    tabindex="0"
                    on:click={() => push(`/charity-profile/${charity.uuid}`)}
                    on:keydown={(e) => e.key === 'Enter' && push(`/charity-profile/${charity.uuid}`)}
                  >
                    <div class="charity-rank">#{index + 1}</div>
                    <div class="charity-info">
                      <div class="charity-name">{charity.name}</div>
                      <div class="charity-amount">{formatCurrency(charity.totalDonation)}</div>
                    </div>
                  </div>
                {/each}
              </div>
            {:else}
              <div class="no-charities">
                <p>No charity donation data available</p>
              </div>
            {/if}
          </div>
        </div>
      </div>

      {#if recentRequests.length > 0}
        <div class="recent-requests">
          <Table
            title="Recent Requests"
            actionText="View All"
            columns={tableColumns}
            data={recentRequests}
            onActionClick={viewAllDonations}
            onRowClick={handleRowClick}
          />
        </div>
      {/if}
    </section>
  {/if}
</div>

<style>
  .admin-dashboard {
    padding: 2rem 0;
  }

  .dashboard-header {
    margin-bottom: 2rem;
    padding: 0 2rem;
  }

  .subtitle {
    color: #6b7280;
    font-size: 0.875rem;
    margin: 0;
    line-height: 1.5;
  }

  .dashboard-alerts {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    margin: 0 2rem 2rem;
  }

  .action-banners-grid,
  .verification-links-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 1rem;
  }

  .dashboard-banner,
  .banner-full-width {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1.5rem;
    padding: 1.25rem 1.5rem;
    border-radius: 12px;
    min-width: 0;
  }

  .banner-full-width {
    width: 100%;
  }

  .pending-payouts-banner {
    border: 1px solid #fca5a5;
    background: linear-gradient(135deg, #fef2f2 0%, #fff 72%);
    box-shadow: 0 4px 14px rgba(252, 108, 107, 0.12);
  }

  .pending-confirmation-banner {
    border: 1px solid #c4b5fd;
    background: linear-gradient(135deg, #f5f3ff 0%, #fff 72%);
    box-shadow: 0 4px 14px rgba(139, 92, 246, 0.12);
  }

  .unconfirmed-bank-banner {
    border: 1px solid #fcd34d;
    background: linear-gradient(135deg, #fffbeb 0%, #fff 72%);
    box-shadow: 0 4px 14px rgba(252, 178, 1, 0.12);
  }

  .verification-link-card {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1.5rem;
    padding: 1.25rem 1.5rem;
    border-radius: 12px;
    text-decoration: none;
    color: inherit;
    min-width: 0;
    transition: border-color 0.2s ease, box-shadow 0.2s ease, transform 0.1s ease;
  }

  .verification-link-card:hover {
    transform: translateY(-1px);
  }

  .verification-link-card:focus-visible {
    outline: 2px solid var(--color-primary, #ffb400);
    outline-offset: 2px;
  }

  .charities-pending-card {
    border: 1px solid #93c5fd;
    background: linear-gradient(135deg, #eff6ff 0%, #fff 72%);
    box-shadow: 0 4px 14px rgba(59, 130, 246, 0.1);
  }

  .charities-pending-card:hover {
    border-color: #60a5fa;
    box-shadow: 0 6px 18px rgba(59, 130, 246, 0.14);
  }

  .employers-pending-card {
    border: 1px solid #86efac;
    background: linear-gradient(135deg, #ecfdf5 0%, #fff 72%);
    box-shadow: 0 4px 14px rgba(42, 157, 143, 0.1);
  }

  .employers-pending-card:hover {
    border-color: #4ade80;
    box-shadow: 0 6px 18px rgba(42, 157, 143, 0.14);
  }

  .verification-link-copy {
    flex: 1;
    min-width: 0;
  }

  .verification-link-eyebrow {
    display: inline-block;
    margin-bottom: 0.35rem;
    font-size: 0.6875rem;
    font-weight: 600;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: #6b7280;
  }

  .charities-pending-card .verification-link-eyebrow {
    color: #1d4ed8;
  }

  .employers-pending-card .verification-link-eyebrow {
    color: #047857;
  }

  .verification-link-title {
    margin: 0 0 0.35rem;
    font-size: 1.125rem;
    font-weight: 600;
    color: #111827;
  }

  .verification-link-text {
    margin: 0;
    font-size: 0.875rem;
    color: #6b7280;
    line-height: 1.5;
  }

  .verification-link-action {
    flex-shrink: 0;
    padding: 0.625rem 1.25rem;
    border-radius: var(--border-radius-button, 10px);
    background: var(--color-primary, #ffb400);
    color: #111827;
    font-size: 0.875rem;
    font-weight: 600;
    white-space: nowrap;
  }

  .banner-copy {
    flex: 1;
    min-width: 0;
  }

  .pending-payouts-banner .banner-eyebrow {
    display: inline-block;
    margin-bottom: 0.35rem;
    font-size: 0.6875rem;
    font-weight: 600;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: #b91c1c;
  }

  .pending-payouts-banner .banner-title {
    margin: 0 0 0.35rem;
    font-size: 1.125rem;
    font-weight: 600;
    color: #991b1b;
  }

  .pending-payouts-banner .banner-text {
    margin: 0;
    font-size: 0.875rem;
    color: #b91c1c;
    line-height: 1.5;
    max-width: 40rem;
  }

  .pending-confirmation-banner .banner-eyebrow {
    display: inline-block;
    margin-bottom: 0.35rem;
    font-size: 0.6875rem;
    font-weight: 600;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: #6d28d9;
  }

  .pending-confirmation-banner .banner-title {
    margin: 0 0 0.35rem;
    font-size: 1.125rem;
    font-weight: 600;
    color: #4c1d95;
  }

  .pending-confirmation-banner .banner-text {
    margin: 0;
    font-size: 0.875rem;
    color: #5b21b6;
    line-height: 1.5;
    max-width: 40rem;
  }

  .unconfirmed-bank-banner .banner-eyebrow {
    display: inline-block;
    margin-bottom: 0.35rem;
    font-size: 0.6875rem;
    font-weight: 600;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: #b45309;
  }

  .unconfirmed-bank-banner .banner-title {
    margin: 0 0 0.35rem;
    font-size: 1.125rem;
    font-weight: 600;
    color: #92400e;
  }

  .unconfirmed-bank-banner .banner-text {
    margin: 0;
    font-size: 0.875rem;
    color: #a16207;
    line-height: 1.5;
    max-width: 40rem;
  }

  .banner-action {
    flex-shrink: 0;
    padding: 0.625rem 1.25rem;
    border: none;
    border-radius: var(--border-radius-button, 10px);
    background: var(--color-primary, #ffb400);
    color: #111827;
    font-size: 0.875rem;
    font-weight: 600;
    cursor: pointer;
    transition: background-color 0.2s ease, transform 0.1s ease;
    white-space: nowrap;
  }

  .banner-action:hover {
    background: var(--color-primary-hover, #f0a800);
  }

  .banner-action:active {
    transform: translateY(1px);
  }

  .banner-action:focus {
    outline: none;
  }

  .banner-action:focus-visible {
    outline: 2px solid var(--color-primary, #ffb400);
    outline-offset: 2px;
  }

  .metrics-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 1rem;
    margin-bottom: 3rem;
    padding: 0 2rem;
  }

  .metric-card {
    background: white;
    border-radius: 12px;
    padding: 1rem 1.25rem;
    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);
    border: 1px solid #f3f4f6;
    transition: transform 0.2s, box-shadow 0.2s;
    min-height: 90px;
    position: relative;
    overflow: hidden;
  }

  a.metric-card-link {
    text-decoration: none;
    color: inherit;
    cursor: pointer;
    display: block;
  }

  a.metric-card-link:focus-visible {
    outline: 2px solid var(--color-primary, #ffb400);
    outline-offset: 2px;
  }

  .metric-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  }

  .metric-content {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    height: 100%;
  }

  .metric-left {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .metric-label-container {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .metric-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    flex-shrink: 0;
  }

  .metric-label {
    font-size: 0.875rem;
    font-weight: 500;
    color: #6b7280;
    margin: 0;
  }

  .metric-value {
    font-size: 2rem;
    font-weight: 700;
    color: #111827;
    line-height: 1;
    margin-top: 0.75rem;
  }

  .metric-icon {
    width: 2.75rem;
    height: 2.75rem;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  .icon {
    width: 1.5rem;
    height: 1.5rem;
  }

  .dashboard-content {
    padding: 0 2rem;
    margin-bottom: 3rem;
  }

  .tabs-container {
    margin-bottom: 1.5rem;
  }

  .tabs {
    display: flex;
    gap: 1rem;
    border-bottom: 1px solid #e5e7eb;
    padding-bottom: 1px;
    overflow-x: auto;
  }

  .tab-button {
    padding: 0.75rem 1.5rem;
    font-size: 0.875rem;
    font-weight: 500;
    color: #6b7280;
    background: none;
    border: none;
    border-bottom: 2px solid transparent;
    cursor: pointer;
    transition: all 0.2s ease;
    white-space: nowrap;
  }

  .tab-button:hover {
    color: #111827;
  }

  .tab-button.active {
    color: #f0b429;
    border-bottom-color: #f0b429;
  }

  .overview-section {
    padding: 0 2rem;
  }

  .section-heading {
    font-size: 1.125rem;
    font-weight: 600;
    color: #111827;
    margin: 0 0 1.25rem;
  }

  .overview-metrics-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 1rem;
    margin-bottom: 2rem;
  }

  .overview-metric-card {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    background: white;
    border: 1px solid #e5e7eb;
    border-radius: 12px;
    padding: 1rem 1.25rem;
    text-decoration: none;
    color: inherit;
    transition: border-color 0.2s ease, box-shadow 0.2s ease;
  }

  .overview-metric-card:hover {
    border-color: var(--color-primary, #ffb400);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  }

  .overview-metric-label {
    font-size: 0.875rem;
    font-weight: 500;
    color: #6b7280;
  }

  .overview-metric-value {
    font-size: 1.5rem;
    font-weight: 700;
    color: #111827;
  }

  .loading-container,
  .error-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 60vh;
    text-align: center;
    margin: 0 2rem;
  }

  .loading-spinner {
    width: 40px;
    height: 40px;
    border: 4px solid #e5e7eb;
    border-left-color: var(--color-primary, #ffb400);
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin-bottom: 1rem;
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }

  .error-container {
    background: white;
    border-radius: 12px;
    padding: 3rem;
    border: 1px solid #e5e7eb;
    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  }

  .error-icon {
    font-size: 3rem;
    margin-bottom: 1rem;
  }

  .error-container h3 {
    font-size: 1.5rem;
    font-weight: 600;
    color: #111827;
    margin-bottom: 0.5rem;
  }

  .error-container p {
    color: #6b7280;
    margin-bottom: 2rem;
    max-width: 400px;
  }

  .retry-button {
    background: var(--color-primary, #ffb400);
    color: #111827;
    border: none;
    border-radius: 8px;
    padding: 0.75rem 1.5rem;
    font-weight: 600;
    cursor: pointer;
  }

  .charts-section {
    display: grid;
    grid-template-columns: 2fr 1fr;
    gap: 1.5rem;
    margin-bottom: 2rem;
  }

  .chart-card {
    background: white;
    border-radius: 12px;
    padding: 1.5rem;
    border: 1px solid #e5e7eb;
    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  }

  .chart-header h3 {
    font-size: 1.125rem;
    font-weight: 600;
    color: #111827;
    margin: 0 0 1.5rem;
  }

  .chart-wrapper {
    display: flex;
    min-height: 200px;
    position: relative;
  }

  .y-axis-labels {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    width: 60px;
    margin-right: 16px;
    padding-top: 8px;
    padding-bottom: 35px;
    flex-shrink: 0;
  }

  .y-label {
    font-size: 12px;
    color: #6c727f;
    font-weight: 500;
  }

  .chart-bars-area {
    flex: 1;
    position: relative;
    padding-top: 8px;
    padding-bottom: 35px;
    min-height: 228px;
  }

  .grid-lines {
    position: absolute;
    inset: 0;
    pointer-events: none;
  }

  .grid-line {
    position: absolute;
    left: 0;
    right: 0;
    height: 1px;
    background: repeating-linear-gradient(
      to right,
      #e5e7eb 0px,
      #e5e7eb 1px,
      transparent 1px,
      transparent 3px
    );
  }

  .bars-wrapper {
    display: flex;
    align-items: end;
    justify-content: space-between;
    height: 100%;
    gap: 4px;
    padding: 0 8px;
    position: relative;
    min-height: 228px;
    border-bottom: 1px solid #d3d5da;
    border-left: 1px solid #d3d5da;
  }

  .bar-column {
    flex: 1;
    position: relative;
    height: 100%;
    display: flex;
    align-items: end;
    max-width: 49px;
  }

  .bar {
    width: 100%;
    position: absolute;
    bottom: 0;
    left: 0;
  }

  .background-bar {
    background: #f4f4f6;
    height: 100% !important;
    border-radius: 12px 12px 0 0;
  }

  .foreground-bar {
    background: var(--color-secondary, #2a9d8f);
    border-radius: 12px 12px 0 0;
    position: relative;
  }

  .bar-value {
    position: absolute;
    top: -20px;
    left: 50%;
    transform: translateX(-50%);
    font-size: 10px;
    color: #374151;
    font-weight: 600;
    white-space: nowrap;
  }

  .overflow-indicator {
    position: absolute;
    top: -8px;
    right: -4px;
    background: #ef4444;
    color: white;
    border-radius: 50%;
    width: 16px;
    height: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 10px;
    font-weight: 700;
  }

  .x-axis-labels {
    display: flex;
    justify-content: space-between;
    margin-top: 16px;
    padding: 0 8px;
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
  }

  .x-label {
    flex: 1;
    text-align: center;
    font-size: 11px;
    color: #6c727f;
    font-weight: 500;
  }

  .top-charities-content {
    padding: 1rem 0;
  }

  .charities-list {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .charity-item {
    display: flex;
    align-items: center;
    gap: 1rem;
    padding: 1rem;
    background: #f9fafb;
    border-radius: 8px;
    border: 1px solid #e5e7eb;
    cursor: pointer;
  }

  .charity-item:hover {
    background: #f3f4f6;
    border-color: var(--color-primary, #ffb400);
  }

  .charity-rank {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background: var(--color-primary, #ffb400);
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 700;
    font-size: 0.875rem;
    flex-shrink: 0;
  }

  .charity-info {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }

  .charity-name {
    font-size: 0.875rem;
    font-weight: 500;
    color: #111827;
  }

  .charity-amount {
    font-size: 1rem;
    font-weight: 700;
    color: #059669;
  }

  .no-charities {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 200px;
    color: #6b7280;
    font-size: 0.875rem;
  }

  .recent-requests {
    margin-bottom: 2rem;
  }

  :global(.request-id) {
    font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
    font-size: 0.875rem;
    color: #6b7280;
  }

  :global(.waiting-on) {
    display: inline-flex;
    align-items: center;
    padding: 0.25rem 0.625rem;
    border-radius: 9999px;
    background: #f3f4f6;
    color: #374151;
    font-size: 0.8125rem;
    font-weight: 500;
  }

  :global(.employee-name) {
    font-size: 0.875rem;
    color: #374151;
  }

  .bg-red-custom {
    background-color: #fc6c6b;
  }

  .bg-purple-custom {
    background-color: #8b5cf6;
  }

  .bg-amber-custom {
    background-color: #fcb201;
  }

  .text-white {
    color: white;
  }

  @media (max-width: 1279px) {
    .charts-section {
      grid-template-columns: 1fr;
    }
  }

  @media (max-width: 768px) {
    .admin-dashboard {
      padding: 0;
    }

    .dashboard-header,
    .dashboard-content,
    .metrics-grid,
    .overview-section,
    .dashboard-alerts {
      padding-left: 0;
      padding-right: 0;
    }

    .dashboard-alerts {
      margin-left: 0;
      margin-right: 0;
    }

    .action-banners-grid,
    .verification-links-grid {
      grid-template-columns: 1fr;
    }

    .dashboard-banner,
    .banner-full-width,
    .verification-link-card {
      flex-direction: column;
      align-items: stretch;
    }

    .banner-action,
    .verification-link-action {
      width: 100%;
      text-align: center;
    }

    .metrics-grid,
    .overview-metrics-grid {
      grid-template-columns: 1fr;
      gap: 0.75rem;
      margin-bottom: 1.5rem;
    }

    .metric-value {
      font-size: 1.5rem;
    }

    .tabs {
      gap: 0;
    }

    .tab-button {
      flex: 1;
      text-align: center;
      padding: 0.625rem 0.75rem;
      font-size: 0.8125rem;
    }
  }
</style>
